import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ReactComponent as EuropeMap } from "../../../../assets/maps/europe-map.svg";
import onestoLogo from "../../../../assets/partners/onesto.svg";
import escLogo from "../../../../assets/partners/enersave-capital.svg";
import uciLogo from "../../../../assets/partners/uci.svg";
import euroPaceLogo from "../../../../assets/partners/fep.svg";
import iccsLogo from "../../../../assets/partners/iccs.svg";
import "./PartnersSection.css";

/* =========================================================*
 *   PARTNER COUNTRIES*
 *   ========================================================= */

const PARTNER_COUNTRIES = {
  ES: {
    countryKey: "partners.countries.spain",

    focusPadding: 1.18,

    markerOffset: {
      x: -4,

      y: 0,
    },

    partners: [
      {
        id: "uci",
        nameKey: "partners.organisations.uci.name",
        typeKey: "partners.organisations.uci.type",
        descriptionKey: "partners.organisations.uci.description",
        websiteKey: "partners.organisations.uci.website",
        logo: uciLogo,
      },
      {
        id: "europace",
        nameKey: "partners.organisations.europace.name",
        typeKey: "partners.organisations.europace.type",
        descriptionKey: "partners.organisations.europace.description",
        websiteKey: "partners.organisations.europace.website",
        logo: euroPaceLogo,
      },
    ],
  },

  BE: {
    countryKey: "partners.countries.belgium",
    focusPadding: 2.35,
    markerOffset: {
      x: -52,
      y: -25,
    },

    partners: [
      {
        id: "onesto",
        nameKey: "partners.organisations.onesto.name",
        typeKey: "partners.organisations.onesto.type",
        descriptionKey: "partners.organisations.onesto.description",
        websiteKey: "partners.organisations.onesto.website",
        logo: onestoLogo,
      },
    ],
  },

  LU: {
    countryKey: "partners.countries.luxembourg",
    focusPadding: 2.6,
    markerOffset: {
      x: 48,
      y: -8,
    },
    partners: [
      {
        id: "enersave",
        nameKey: "partners.organisations.enersave.name",
        typeKey: "partners.organisations.enersave.type",
        descriptionKey: "partners.organisations.enersave.description",
        websiteKey: "partners.organisations.enersave.website",
        logo: escLogo,
      },
    ],
  },
  GR: {
    countryKey: "partners.countries.greece",
    focusPadding: 1.22,
    markerOffset: {
      x: 13,
      y: 9,
    },
    partners: [
      {
        id: "iccs",
        nameKey: "partners.organisations.iccs.name",
        typeKey: "partners.organisations.iccs.type",
        descriptionKey: "partners.organisations.iccs.description",
        websiteKey: "partners.organisations.iccs.website",
        logo: iccsLogo,
      },
    ],
  },
};

/* =========================================================*
 *   ONBOARDING OPTIONS*
 *   ========================================================= */

const JOIN_OPTIONS = [
  {
    id: "oss",
    number: "01",
    titleKey: "partners.join.options.oss.title",
    descriptionKey: "partners.join.options.oss.description",
    href: "/register/oss/step-1",
  },
  {
    id: "lender",
    number: "02",
    titleKey: "partners.join.options.lender.title",
    descriptionKey: "partners.join.options.lender.description",
    href: "/register/lender",
  },
  {
    id: "investor",
    number: "03",
    titleKey: "partners.join.options.investor.title",
    descriptionKey: "partners.join.options.investor.description",
  },
];

export default function PartnersSection() {
  const { t } = useTranslation("home");

  /* =========================================================*
   *     REFS*
   *     ========================================================= */

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const mapRef = useRef(null);
  const mapViewportRef = useRef(null);
  const panelRef = useRef(null);
  const joinRef = useRef(null);
  const markerRefs = useRef({});
  const baseViewBoxRef = useRef(null);
  const animatedViewBoxRef = useRef(null);
  const selectedCountryRef = useRef(null);
  const panelScrollRef = useRef(null);
  const isResettingRef = useRef(false);

  const [selectedCountry, setSelectedCountry] = useState(null);

  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  const [isReturning, setIsReturning] = useState(false);

  const getSvg = useCallback(() => {
    return mapViewportRef.current?.querySelector("svg") || null;
  }, []);

  const getCountryElement = useCallback(
    (code) => {
      const svg = getSvg();

      if (!svg) {
        return null;
      }

      return svg.querySelector(`[id="${code}"]`) || null;
    },

    [getSvg],
  );

  /* =========================================================*
   *     CREATE FITTED VIEWBOX*
   *     ========================================================= */

  const createFittedViewBox = useCallback(
    ({
      bbox,

      padding = 1.5,

      horizontalAnchor = 0.5,

      minimumSize = 1,
    }) => {
      const viewport = mapViewportRef.current;

      if (!viewport) {
        return null;
      }

      const rect = viewport.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        return null;
      }

      const aspect = rect.width / rect.height;

      let width = Math.max(bbox.width, minimumSize) * padding;

      let height = Math.max(bbox.height, minimumSize) * padding;

      if (width / height > aspect) {
        height = width / aspect;
      } else {
        width = height * aspect;
      }

      const centreX = bbox.x + bbox.width / 2;

      const centreY = bbox.y + bbox.height / 2;

      return {
        x: centreX - width * horizontalAnchor,

        y: centreY - height / 2,

        width,

        height,
      };
    },

    [],
  );

  /* =========================================================*
   *     EUROPE OVERVIEW*
   *     ========================================================= */

  const buildOverviewViewBox = useCallback(() => {
    const countries = Object.keys(PARTNER_COUNTRIES)

      .map((code) => getCountryElement(code))

      .filter(Boolean);

    if (!countries.length) {
      return null;
    }

    const boxes = countries.map((country) => country.getBBox());

    const minX = Math.min(...boxes.map((box) => box.x));

    const minY = Math.min(...boxes.map((box) => box.y));

    const maxX = Math.max(...boxes.map((box) => box.x + box.width));

    const maxY = Math.max(...boxes.map((box) => box.y + box.height));

    return createFittedViewBox({
      bbox: {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      },
      padding: 1.28,
      horizontalAnchor: 0.5,
      minimumSize: 1,
    });
  }, [createFittedViewBox, getCountryElement]);

  /* =========================================================*
   *     MARKER POSITIONS*
   *     ========================================================= */

  const updateMarkerPositions = useCallback(() => {
    const viewport = mapViewportRef.current;

    const svg = getSvg();

    if (!viewport || !svg) {
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();

    let offsetScale = 1;

    if (window.innerWidth <= 660) {
      offsetScale = 0.5;
    } else if (window.innerWidth <= 900) {
      offsetScale = 0.72;
    }

    Object.entries(PARTNER_COUNTRIES).forEach(([code, config]) => {
      const country = getCountryElement(code);

      const marker = markerRefs.current[code];

      if (!country || !marker) {
        return;
      }

      try {
        const bbox = country.getBBox();

        const ctm = country.getScreenCTM();

        if (!ctm) {
          return;
        }

        const point = svg.createSVGPoint();

        point.x = bbox.x + bbox.width / 2;

        point.y = bbox.y + bbox.height / 2;

        const screenPoint = point.matrixTransform(ctm);

        const active = selectedCountryRef.current === code;

        const multiplier = active ? 0.18 : 1;

        const offsetX =
          (config.markerOffset?.x || 0) * offsetScale * multiplier;

        const offsetY =
          (config.markerOffset?.y || 0) * offsetScale * multiplier;

        marker.style.left = `${screenPoint.x - viewportRect.left + offsetX}px`;

        marker.style.top = `${screenPoint.y - viewportRect.top + offsetY}px`;
      } catch (error) {
        console.warn(`Unable to position marker for ${code}`, error);
      }
    });
  }, [getCountryElement, getSvg]);

  /* =========================================================*

*     VIEWBOX ANIMATION*

*     ========================================================= */

  const animateViewBox = useCallback(
    (
      target,
      {
        duration = 1,
        ease = "power4.inOut",
        onComplete,
        updateMarkers = true,
      } = {},
    ) => {
      const svg = getSvg();
      const current = animatedViewBoxRef.current;

      if (!svg || !current || !target) {
        return;
      }

      gsap.killTweensOf(current);

      gsap.to(current, {
        x: target.x,
        y: target.y,
        width: target.width,
        height: target.height,
        duration,
        ease,

        onUpdate: () => {
          svg.setAttribute(
            "viewBox",
            [current.x, current.y, current.width, current.height].join(" "),
          );

          if (updateMarkers) {
            updateMarkerPositions();
          }
        },

        onComplete: () => {
          updateMarkerPositions();
          onComplete?.();
        },
      });
    },
    [getSvg, updateMarkerPositions],
  );

  /* =========================================================*

*     FOCUS COUNTRY*

*     ========================================================= */

  const focusCountry = useCallback(
    (code, partnerId = null) => {
      if (isResettingRef.current) {
        return;
      }

      const country = getCountryElement(code);
      const svg = getSvg();
      const config = PARTNER_COUNTRIES[code];

      if (!country || !svg || !config) {
        return;
      }

      const bbox = country.getBBox();

      const horizontalAnchor = window.innerWidth > 900 ? 0.255 : 0.5;

      const target = createFittedViewBox({
        bbox,
        padding: config.focusPadding,
        horizontalAnchor,
        minimumSize: code === "LU" ? 4.5 : code === "BE" ? 3.1 : 1,
      });

      if (!target) {
        return;
      }

      svg.querySelectorAll(".partner-map__country").forEach((element) => {
        element.classList.remove("is-country-active");
      });

      country.classList.add("is-country-active");

      selectedCountryRef.current = code;

      setIsReturning(false);
      setSelectedCountry(code);

      setSelectedPartnerId(partnerId || config.partners[0]?.id || null);

      animateViewBox(target, {
        duration: 1.12,
        ease: "expo.inOut",
      });
    },
    [animateViewBox, createFittedViewBox, getCountryElement, getSvg],
  );

  /* =========================================================*
   *     RETURN TO EUROPE*
   *     ========================================================= */

  const resetMap = useCallback(() => {
    const svg = getSvg();

    const currentCountry = selectedCountryRef.current;

    if (
      !svg ||
      !baseViewBoxRef.current ||
      !currentCountry ||
      isResettingRef.current
    ) {
      return;
    }

    isResettingRef.current = true;

    setIsReturning(true);

    setSelectedPartnerId(null);

    svg.querySelectorAll(".partner-map__country").forEach((element) => {
      element.classList.remove("is-country-active");
    });

    selectedCountryRef.current = null;

    if (panelRef.current) {
      gsap.to(panelRef.current, {
        xPercent: 8,
        autoAlpha: 0,
        duration: 0.42,
        ease: "power2.in",
        overwrite: "auto",
      });
    }

    animateViewBox(baseViewBoxRef.current, {
      duration: 1.15,
      ease: "expo.inOut",
      updateMarkers: false,

      onComplete: () => {
        updateMarkerPositions();

        const markerVisuals = mapRef.current?.querySelectorAll(
          ".partner-map__marker-visual",
        );

        if (markerVisuals?.length) {
          gsap.killTweensOf(markerVisuals);
        }

        setSelectedCountry(null);
        setIsReturning(false);
        isResettingRef.current = false;
      },
    });
  }, [animateViewBox, getSvg, updateMarkerPositions]);

  /* =========================================================*

*     INITIALISE SVG*

*     ========================================================= */

  useLayoutEffect(() => {
    const svg = getSvg();

    if (!svg) {
      return undefined;
    }

    svg.querySelectorAll("path").forEach((path) => {
      path.classList.add("partner-map__country-shape");
    });

    const cleanups = [];

    Object.entries(PARTNER_COUNTRIES).forEach(([code, config]) => {
      const country = getCountryElement(code);

      if (!country) {
        console.warn(`Country ${code} not found in Europe SVG.`);
        return;
      }

      country.classList.add("partner-map__country");
      country.setAttribute("role", "button");
      country.setAttribute("tabindex", "0");
      country.setAttribute(
        "aria-label",
        t("partners.map.viewCountry", {
          country: t(config.countryKey),
        }),
      );

      const clickHandler = (event) => {
        event.stopPropagation();
        focusCountry(code);
      };

      const keyHandler = (event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        focusCountry(code);
      };

      country.addEventListener("click", clickHandler);
      country.addEventListener("keydown", keyHandler);

      cleanups.push(() => {
        country.removeEventListener("click", clickHandler);
        country.removeEventListener("keydown", keyHandler);
      });
    });

    let frame2 = null;

    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        const overview = buildOverviewViewBox();

        if (overview) {
          baseViewBoxRef.current = overview;

          animatedViewBoxRef.current = {
            ...overview,
          };

          svg.setAttribute(
            "viewBox",
            [overview.x, overview.y, overview.width, overview.height].join(" "),
          );
        }

        updateMarkerPositions();
      });
    });

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            updateMarkerPositions();
          })
        : null;

    if (mapViewportRef.current) {
      resizeObserver?.observe(mapViewportRef.current);
    }

    return () => {
      cancelAnimationFrame(frame1);

      if (frame2) {
        cancelAnimationFrame(frame2);
      }

      resizeObserver?.disconnect();

      cleanups.forEach((cleanup) => cleanup());
    };
  }, [
    buildOverviewViewBox,
    focusCountry,
    getCountryElement,
    getSvg,
    t,
    updateMarkerPositions,
  ]);

  /* =========================================================*

*     ESC KEY*

*     ========================================================= */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && selectedCountryRef.current) {
        resetMap();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [resetMap]);

  /* =========================================================*

*     EMPTY MAP CLICK*

*     ========================================================= */

  const handleViewportClick = (event) => {
    if (event.target.closest?.(".partner-map__country-panel")) {
      return;
    }

    if (event.target.closest?.(".partner-map__country")) {
      return;
    }

    if (event.target.closest?.(".partner-map__marker-logo--button")) {
      return;
    }

    if (selectedCountryRef.current) {
      resetMap();
    }
  };

  useEffect(() => {
    const panel = panelRef.current;
    const scroller = panelScrollRef.current;

    if (!panel || !scroller || !selectedCountry) {
      return undefined;
    }

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();

      scroller.scrollTop += event.deltaY;
    };

    panel.addEventListener("wheel", handleWheel, {
      passive: false,
    });

    return () => {
      panel.removeEventListener("wheel", handleWheel);
    };
  }, [selectedCountry]);

  /* =========================================================
   FOCUSED COUNTRY PANEL REVEAL
   ========================================================= */

  useLayoutEffect(() => {
    if (!selectedCountry || !panelRef.current || isReturning) {
      return undefined;
    }

    const panel = panelRef.current;

    const context = gsap.context(() => {
      const tl = gsap.timeline();

      tl.fromTo(
        panel,
        {
          xPercent: 8,
          autoAlpha: 0,
        },
        {
          xPercent: 0,
          autoAlpha: 1,
          duration: 0.58,
          ease: "power3.out",
        },
      );

      tl.fromTo(
        ".partner-map__panel-label",
        {
          y: 12,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.36,
        },
        "-=0.3",
      );

      tl.fromTo(
        ".partner-map__panel-country h3",
        {
          y: 20,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.48,
          ease: "power3.out",
        },
        "-=0.22",
      );

      tl.fromTo(
        ".partner-map__partner-logo",
        {
          scale: 0.86,
          y: 18,
          autoAlpha: 0,
        },
        {
          scale: 1,
          y: 0,
          autoAlpha: 1,
          stagger: 0.08,
          duration: 0.5,
          ease: "back.out(1.4)",
        },
        "-=0.2",
      );

      tl.fromTo(
        [
          ".partner-map__partner-type",
          ".partner-map__partner-card h4",
          ".partner-map__partner-description",
          ".partner-map__website",
        ],
        {
          y: 15,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.055,
          duration: 0.4,
          ease: "power2.out",
        },
        "-=0.3",
      );

      tl.fromTo(
        ".partner-map__close-hint",
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
        },
        "-=0.1",
      );
    }, panel);

    return () => context.revert();
  }, [selectedCountry, isReturning]);

  /* =========================================================
   PARTNERS VIEWPORT REVEAL

   Uses live DOM geometry rather than ScrollTrigger.
   The actual map viewport is never transformed here.
   ========================================================= */

  useLayoutEffect(() => {
    const root = sectionRef.current;
    const header = headerRef.current;
    const map = mapRef.current;
    const join = joinRef.current;

    if (!root || !header || !map || !join) {
      return undefined;
    }

    const headerElements = [
      header.querySelector(".partners-section__eyebrow"),
      header.querySelector(".partners-section__title"),
      header.querySelector(".partners-section__lead"),
    ].filter(Boolean);

    const joinHeading = join.querySelector(".partners-join__heading") || join;

    const joinHeadingElements = [
      join.querySelector(".partners-join__eyebrow"),
      join.querySelector(".partners-join__heading h3"),
      join.querySelector(".partners-join__lead"),
    ].filter(Boolean);

    const joinCards = Array.from(join.querySelectorAll(".partners-join__card"));

    const animatedElements = [
      ...headerElements,
      map,
      ...joinHeadingElements,
      ...joinCards,
    ];

    gsap.killTweensOf(animatedElements);

    gsap.set(animatedElements, {
      opacity: 1,
      visibility: "visible",
      y: 0,
      scale: 1,
    });

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion) {
      return undefined;
    }

    const clamp = gsap.utils.clamp(0, 1);

    const groups = [
      {
        trigger: header,
        elements: headerElements,
        amount: 30,
        scale: 0.99,
        opacityOnly: false,
      },
      {
        trigger: map,
        elements: [map],
        amount: 0,
        scale: 1,
        opacityOnly: true,
      },
      {
        trigger: joinHeading,
        elements: joinHeadingElements,
        amount: 30,
        scale: 0.99,
        opacityOnly: false,
      },
      {
        trigger: join,
        elements: joinCards,
        amount: 34,
        scale: 0.98,
        opacityOnly: false,
      },
    ];

    const applyGroup = (group, viewportHeight) => {
      const rect = group.trigger.getBoundingClientRect();
      const travel = viewportHeight + rect.height;

      if (travel <= 0) {
        return;
      }

      const progress = clamp((viewportHeight - rect.top) / travel);

      const enterStart = 0.12;
      const enterEnd = 0.34;
      const exitStart = 0.88;

      let opacity = 1;
      let y = 0;
      let currentScale = 1;

      if (progress < enterStart) {
        opacity = 0;

        if (!group.opacityOnly) {
          y = group.amount;
          currentScale = group.scale;
        }
      } else if (progress < enterEnd) {
        const q = clamp((progress - enterStart) / (enterEnd - enterStart));

        opacity = q;

        if (!group.opacityOnly) {
          y = group.amount * (1 - q);
          currentScale = group.scale + (1 - group.scale) * q;
        }
      } else if (progress > exitStart) {
        const q = clamp((progress - exitStart) / (1 - exitStart));

        opacity = 1 - q;

        if (!group.opacityOnly) {
          y = -group.amount * q;
          currentScale = 1 - (1 - group.scale) * q;
        }
      }

      group.elements.forEach((element, index) => {
        gsap.set(element, {
          opacity,
          visibility: opacity <= 0.001 ? "hidden" : "visible",
          y: group.opacityOnly ? 0 : y * (1 + index * 0.025),
          scale: group.opacityOnly ? 1 : currentScale,
          force3D: true,
        });
      });
    };

    const update = () => {
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      groups.forEach((group) => {
        applyGroup(group, viewportHeight);
      });
    };

    let updateFrame = null;

    const scheduleUpdate = () => {
      if (updateFrame) {
        return;
      }

      updateFrame = requestAnimationFrame(() => {
        updateFrame = null;
        update();
      });
    };

    window.addEventListener("scroll", scheduleUpdate, {
      passive: true,
    });

    window.addEventListener("resize", scheduleUpdate);

    update();

    const initialFrame = requestAnimationFrame(() => {
      update();
      updateMarkerPositions();
    });

    return () => {
      cancelAnimationFrame(initialFrame);

      if (updateFrame) {
        cancelAnimationFrame(updateFrame);
      }

      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);

      gsap.set(animatedElements, {
        opacity: 1,
        visibility: "visible",
        y: 0,
        scale: 1,
      });
    };
  }, [updateMarkerPositions]);

  /* =========================================================*

*     ACTIVE COUNTRY*

*     ========================================================= */

  const activeCountry = selectedCountry
    ? PARTNER_COUNTRIES[selectedCountry]
    : null;

  /* =========================================================*

*     RENDER*

*     ========================================================= */

  return (
    <section ref={sectionRef} className="partners-section">
      <div className="partners-section__inner">
        {/* =================================================*

*            HEADER*

*            ================================================= */}

        <header ref={headerRef} className="partners-section__header">
          <span className="partners-section__eyebrow">
            {t("partners.header.eyebrow")}
          </span>

          <h2 className="partners-section__title">
            {t("partners.header.title")}{" "}
            <strong>{t("partners.header.titleHighlight")}</strong>
          </h2>

          <p className="partners-section__lead">
            {t("partners.header.description")}
          </p>
        </header>

        {/* =================================================*

*            MAP*

*            ================================================= */}

        <div
          ref={mapRef}
          className={[
            "partner-map",

            selectedCountry ? "is-focused" : "",

            isReturning ? "is-returning" : "",
          ]

            .filter(Boolean)

            .join(" ")}
        >
          <div
            ref={mapViewportRef}
            className="partner-map__viewport"
            onClick={handleViewportClick}
          >
            <EuropeMap
              className="partner-map__svg"
              preserveAspectRatio="xMidYMid meet"
              aria-label={t("partners.map.ariaLabel")}
            />

            {/* =============================================*

*                MAP LOGOS*

*                ============================================= */}

            {Object.entries(PARTNER_COUNTRIES).map(([code, country]) => (
              <div
                key={code}
                ref={(element) => {
                  markerRefs.current[code] = element;
                }}
                className={`partner-map__marker ${
                  selectedCountry === code ? "is-active" : ""
                }`}
              >
                <div className="partner-map__marker-visual">
                  <div className="partner-map__marker-logos">
                    {country.partners.map((partner) => (
                      <button
                        key={partner.id}
                        type="button"
                        className="partner-map__marker-logo partner-map__marker-logo--button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();

                          focusCountry(code, partner.id);
                        }}
                        aria-label={t(partner.nameKey)}
                      >
                        <img src={partner.logo} alt={t(partner.nameKey)} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* =============================================*

*                INFORMATION PANEL*

*                ============================================= */}

            {activeCountry && (
              <aside
                ref={panelRef}
                className="partner-map__country-panel"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="partner-map__panel-top">
                  <div className="partner-map__panel-country">
                    <span className="partner-map__panel-label">
                      {t("partners.panel.legupIn")}
                    </span>

                    <h3>{t(activeCountry.countryKey)}</h3>
                  </div>

                  <button
                    type="button"
                    className="partner-map__back"
                    onClick={resetMap}
                    aria-label={t("partners.map.returnToEurope")}
                  >
                    <span aria-hidden="true">←</span>

                    <span>{t("partners.map.europe")}</span>
                  </button>
                </div>
                <div ref={panelScrollRef} className="partner-map__panel-scroll">
                  {activeCountry.partners.length > 1 && (
                    <div className="partner-map__partner-selector">
                      <span className="partner-map__partner-count">
                        {t("partners.panel.participatingPartners", {
                          count: activeCountry.partners.length,
                        })}
                      </span>

                      <div className="partner-map__partner-tabs">
                        {activeCountry.partners.map((partner) => (
                          <button
                            key={partner.id}
                            type="button"
                            className={`partner-map__partner-tab ${
                              selectedPartnerId === partner.id
                                ? "is-active"
                                : ""
                            }`}
                            onClick={() => setSelectedPartnerId(partner.id)}
                          >
                            <img src={partner.logo} alt="" />

                            <span>{t(partner.nameKey)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* =========================================*

*                    SCROLLABLE PANEL*

*                    ========================================= */}

                  <div className="partner-map__partner-list">
                    {activeCountry.partners
                      .filter(
                        (partner) =>
                          partner.id ===
                          (selectedPartnerId || activeCountry.partners[0]?.id),
                      )
                      .map((partner) => (
                        <article
                          key={partner.id}
                          className="partner-map__partner-card"
                        >
                          <div className="partner-map__partner-logo">
                            <img src={partner.logo} alt={t(partner.nameKey)} />
                          </div>

                          <span className="partner-map__partner-type">
                            {t(partner.typeKey)}
                          </span>

                          <h4>{t(partner.nameKey)}</h4>

                          <p className="partner-map__partner-description">
                            {t(partner.descriptionKey)}
                          </p>

                          <a
                            href={t(partner.websiteKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="partner-map__website"
                          >
                            <span className="partner-map__website-label">
                              {t("partners.panel.website")}
                            </span>

                            <span className="partner-map__website-url">
                              {t(partner.websiteKey)}
                            </span>

                            <span
                              className="partner-map__website-arrow"
                              aria-hidden="true"
                            >
                              ↗
                            </span>
                          </a>
                        </article>
                      ))}
                  </div>

                  <span className="partner-map__close-hint">
                    {t("partners.map.closeHint")}
                  </span>
                </div>
              </aside>
            )}
          </div>
        </div>

        {/* =================================================*

*            JOIN LEG-UP*

*            ================================================= */}

        <div ref={joinRef} className="partners-join">
          <div className="partners-join__heading">
            <div className="partners-join__heading-copy">
              <span className="partners-join__eyebrow">
                {t("partners.join.eyebrow")}
              </span>

              <h3>
                {t("partners.join.title")}{" "}
                <strong>{t("partners.join.titleHighlight")}</strong>
              </h3>
            </div>

            <p className="partners-join__lead">
              {t("partners.join.description")}
            </p>
          </div>

          <div className="partners-join__grid">
            {JOIN_OPTIONS.map((item) => (
              <a
                key={item.id}
                href={item.href}
                className={`partners-join__card partners-join__card--${item.id}`}
              >
                <span className="partners-join__number">{item.number}</span>

                <div className="partners-join__content">
                  <h4>{t(item.titleKey)}</h4>

                  <p>{t(item.descriptionKey)}</p>
                </div>

                <span className="partners-join__arrow" aria-hidden="true">
                  ↗
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
